// Enhanced CreateProjectDialog.js - Integration with Copy Feature

import React, { useState, useEffect, useRef } from 'react';
import appConfig from '../config/appConfig';
import CopyProjectDataDialog from './ManageCopyProjectData/CopyProjectDataDialog.jsx';
import ProjectCreationConfirmationDialog from './ManageCopyProjectData/ProjectCreationConfirmationDialog.jsx';

// Inline functional domain creation dialog
const InlineFunctionalDomainDialog = ({ isOpen, onClose, onSubmit, corporateAccount, loginToken, requesting_for_corporate_account }) => {
  const [domainName, setDomainName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const domainInputRef = useRef(null);

  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1100
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '400px',
      maxWidth: '90vw',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '15px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    content: {
      padding: '20px'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px'
    }
  };

  useEffect(() => {
    if (isOpen) {
      setDomainName('');
      setError('');
      setIsLoading(false);
      setTimeout(() => {
        if (domainInputRef.current) {
          domainInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!domainName.trim()) {
      setError('Domain name is required');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/create_functional_domain'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          requesting_for_corporate_account: requesting_for_corporate_account,
          functional_domain: domainName.trim()
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setError('Functional domain created successfully!');
        setTimeout(() => {
          onSubmit(domainName.trim()); // Pass the new domain name back
          onClose();
        }, 1500);
      } else {
        throw new Error(data.status_description || 'Failed to create functional domain');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        <div style={dialogStyles.header}>
          Create New Functional Domain
        </div>

        <div style={dialogStyles.content}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Domain Name: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                ref={domainInputRef}
                type="text"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="Enter functional domain name"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div style={{
                marginBottom: '15px',
                padding: '8px',
                borderRadius: '4px',
                backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
                color: error.includes('successfully') ? '#059669' : '#dc2626',
                border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
          </form>
        </div>

        <div style={dialogStyles.footer}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <svg style={{
                  animation: 'spin 1s linear infinite',
                  marginRight: '8px',
                  width: '16px',
                  height: '16px'
                }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isLoading ? 'Creating...' : 'Create Domain'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateProjectDialog = ({
  isOpen,
  onClose,
  onSubmit,
  selectedAccountId,
  isEditMode,
  projectToEdit,
  permissions,
  loginToken,
  corporateAccount,
  currentProject
}) => {
  const [projectId, setProjectId] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [functionalDomain, setFunctionalDomain] = useState('');
  const [projectPrefix, setProjectPrefix] = useState('');
  const [error, setError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [functionalDomains, setFunctionalDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [isCreateDomainDialogOpen, setIsCreateDomainDialogOpen] = useState(false);

  // New state for copy feature
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);

  const projectIdInputRef = useRef(null);

  // Updated dialog styles with fixed layout
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '500px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Fetch functional domains when dialog opens and account is selected
  const fetchFunctionalDomains = async (accountId) => {
    if (!accountId || !loginToken) return;

    setLoadingDomains(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_functional_domains'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({
          corporate_account: accountId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setFunctionalDomains(data.functional_domain_list || []);
      } else {
        console.error('Failed to fetch functional domains:', data.status_description);
        setFunctionalDomains([]);
      }
    } catch (error) {
      console.error('Error fetching functional domains:', error);
      setFunctionalDomains([]);
    } finally {
      setLoadingDomains(false);
    }
  };

  // Handle new domain creation
  const handleNewDomainCreated = (newDomainName) => {
    // Add the new domain to the list and select it
    const newDomain = { functional_domain: newDomainName };
    setFunctionalDomains(prev => [...prev, newDomain]);
    setFunctionalDomain(newDomainName);
    setIsCreateDomainDialogOpen(false);
  };

  // Focus the project ID field whenever the dialog opens or after successful creation
  useEffect(() => {
    if (isOpen && projectIdInputRef.current) {
      setTimeout(() => {
        projectIdInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset form and focus when dialog opens
  useEffect(() => {
    if (isOpen) {
      setShowValidationErrors(false);
      setError('');

      // Fetch functional domains for the selected account
      if (selectedAccountId) {
        fetchFunctionalDomains(selectedAccountId);
      }

      if (isEditMode && projectToEdit) {
        setProjectId(projectToEdit.project_id);
        setProjectDescription(projectToEdit.project_description);
        setFunctionalDomain(projectToEdit.functional_domain || '');
        setProjectPrefix(projectToEdit.project_prefix || '');
      } else {
        // Reset form for new project creation
        setProjectId('');
        setProjectDescription('');
        setFunctionalDomain('');
        setProjectPrefix('');
      }
    }
  }, [isOpen, isEditMode, projectToEdit, selectedAccountId]);

  const resetFormAfterCreate = () => {
    setProjectId('');
    setProjectDescription('');
    setFunctionalDomain('');
    setProjectPrefix('');
    setShowValidationErrors(false);
    setError('');

    // Focus the project ID field again for next project creation
    setTimeout(() => {
      if (projectIdInputRef.current) {
        projectIdInputRef.current.focus();
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setShowValidationErrors(true);
    setError('');
    setIsLoading(true);

    if (!selectedAccountId) {
      setError('Please select an account before creating a project');
      setIsLoading(false);
      return { success: false };
    }

    // Validation checks
    if (!projectId.trim()) {
      setError('Project name is required');
      setIsLoading(false);
      return { success: false };
    }

    if (!projectDescription.trim()) {
      setError('Project Description is required');
      setIsLoading(false);
      return { success: false };
    }

    if (!functionalDomain.trim()) {
      setError('Functional Domain is required');
      setIsLoading(false);
      return { success: false };
    }

    if (!projectPrefix.trim()) {
      setError('Project Prefix is required');
      setIsLoading(false);
      return { success: false };
    }

    const endpoint = isEditMode ?
      appConfig.getUrl('/update_project') :
      appConfig.getUrl('/create_project');

    // Prepare request body
    const requestBody = {
      corporate_account: corporateAccount,
      requesting_for_corporate_account: selectedAccountId,
      project_id: projectId,
      project_description: projectDescription,
      functional_domain: functionalDomain,
      project_prefix: projectPrefix
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setError(`Project ${isEditMode ? 'updated' : 'created'} successfully`);

        // Call onSubmit to refresh the project list
        onSubmit();

        // For new project creation, show confirmation dialog
        if (!isEditMode) {
          setCreatedProjectId(projectId);
          setShowConfirmationDialog(true);
        } else {
          // For edit mode, close after delay
          setTimeout(() => {
            onClose();
          }, 1500);
        }

        setIsLoading(false);
        return {
          success: true,
          isEditMode: isEditMode,
          projectId: data.project_id || projectId
        };
      } else {
        throw new Error(data.status_description || 'Failed to save project');
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return { success: false };
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationDialog(false);
    setCreatedProjectId(null);
    resetFormAfterCreate();
  };

  const handleProceedToCopy = () => {
    setShowConfirmationDialog(false);
    setShowCopyDialog(true);
  };

  const handleCopyDialogClose = () => {
    setShowCopyDialog(false);
    setCreatedProjectId(null);
    resetFormAfterCreate();
  };

  if (!isOpen) return null;

  return (
    <>
      <div style={dialogStyles.overlay}>
        <div style={dialogStyles.dialog}>
          {/* Fixed Header */}
          <div style={dialogStyles.header}>
            {isEditMode ? `Edit Project (${projectToEdit?.project_id})` : 'Create New Project'}
          </div>

          {/* Scrollable Content */}
          <div style={dialogStyles.content}>
            <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1 }}>
                {/* Project ID */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Project Name: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    ref={projectIdInputRef}
                    type="text"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${showValidationErrors && !projectId.trim() ? '#dc2626' : '#ddd'}`,
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter project Name"
                    disabled={isEditMode}
                    required
                  />
                  {showValidationErrors && !projectId.trim() && (
                    <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Project name is required
                    </span>
                  )}
                </div>

                {/* Project Description */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Project Description: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${showValidationErrors && !projectDescription.trim() ? '#dc2626' : '#ddd'}`,
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter project description"
                    required
                  />
                  {showValidationErrors && !projectDescription.trim() && (
                    <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Project Description is required
                    </span>
                  )}
                </div>

                {/* Functional Domain */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Functional Domain: <span style={{ color: '#dc2626' }}>*</span>
                  </label>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    {loadingDomains ? (
                      <div style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <svg style={{
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px',
                          width: '16px',
                          height: '16px'
                        }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading functional domains...
                      </div>
                    ) : (
                      <>
                        <select
                          value={functionalDomain}
                          onChange={(e) => setFunctionalDomain(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            border: `1px solid ${showValidationErrors && !functionalDomain.trim() ? '#dc2626' : '#ddd'}`,
                            borderRadius: '4px',
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                          required
                        >
                          <option value="">Select a functional domain</option>
                          {functionalDomains.map(domain => (
                            <option key={domain.functional_domain} value={domain.functional_domain}>
                              {domain.functional_domain}
                            </option>
                          ))}
                        </select>

                        {/* Create New Domain Button */}
                        <button
                          type="button"
                          onClick={() => setIsCreateDomainDialogOpen(true)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#e0f2fe',
                            color: '#0277bd',
                            border: '1px solid #81d4fa',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Create new functional domain"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12h14"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>

                  {showValidationErrors && !functionalDomain.trim() && (
                    <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Functional Domain is required
                    </span>
                  )}
                  {functionalDomains.length === 0 && !loadingDomains && selectedAccountId && (
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginTop: '4px'
                    }}>
                      No functional domains available. Click "New" to create one.
                    </div>
                  )}
                </div>

                {/* Project Prefix */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Project Prefix: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={projectPrefix}
                    onChange={(e) => setProjectPrefix(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: `1px solid ${showValidationErrors && !projectPrefix.trim() ? '#dc2626' : '#ddd'}`,
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter project prefix (e.g., TRN, OMS, WMS)"
                    required
                  />
                  {showValidationErrors && !projectPrefix.trim() && (
                    <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Project Prefix is required
                    </span>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Fixed Footer */}
          <div style={dialogStyles.footer}>
            {/* Error message */}
            {error && (
              <div style={{
                marginBottom: '15px',
                padding: '8px',
                borderRadius: '4px',
                backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
                color: error.includes('successfully') ? '#059669' : '#dc2626',
                border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  backgroundColor: '#FFFDD0',
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                disabled={isLoading}
              >
                Close
              </button>

              <button
                type="submit"
                onClick={(e) => {
                  if (!permissions.canUpdate) {
                    e.preventDefault();
                    setError('You do not have permission to create or edit projects');
                    return;
                  }
                  handleSubmit(e);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: !permissions.canUpdate ? '#f3f4f6' : '#C8F4DE',
                  color: !permissions.canUpdate ? '#6b7280' : 'black',
                  border: !permissions.canUpdate ? '1px solid #d1d5db' : 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  opacity: isLoading ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
                disabled={isLoading}
              >
                {isLoading && (
                  <svg style={{
                    animation: 'spin 1s linear infinite',
                    marginLeft: '-4px',
                    marginRight: '8px',
                    width: '16px',
                    height: '16px'
                  }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {!permissions.canUpdate ?
                  (isEditMode ? 'No Permission to Update' : 'No Permission to Create') :
                  (isLoading ? 'Processing...' : (isEditMode ? 'Update Project' : 'Create Project'))
                }
              </button>
            </div>
          </div>
        </div>

        {/* Add CSS for spin animation */}
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Project Creation Confirmation Dialog */}
      <ProjectCreationConfirmationDialog
        isOpen={showConfirmationDialog}
        onClose={handleConfirmationClose}
        onProceedToCopy={handleProceedToCopy}
        projectName={createdProjectId}
      />

      {/* Copy Project Data Dialog */}
      <CopyProjectDataDialog
        isOpen={showCopyDialog}
        onClose={handleCopyDialogClose}
        newProjectId={createdProjectId}
        corporateAccount={corporateAccount}
        requestingForCorporateAccount={selectedAccountId}
        loginToken={loginToken}
      />

      {/* Inline Functional Domain Creation Dialog */}
      <InlineFunctionalDomainDialog
        isOpen={isCreateDomainDialogOpen}
        onClose={() => setIsCreateDomainDialogOpen(false)}
        onSubmit={handleNewDomainCreated}
        corporateAccount={corporateAccount}
        loginToken={loginToken}
        requesting_for_corporate_account={selectedAccountId}
      />
    </>
  );
};

export default CreateProjectDialog;